import React, { useState } from 'react';
import styled from 'styled-components';
import { DesignSystemSurface } from '../surface/DesignSystemSurface';
import { AppShell, ShellNavGroup } from '../shell/AppShell';
import { ThemeToggle } from '../theme/ThemeToggle';
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  SectionHeader,
  Skeleton,
  Tag,
} from '../components';
import {
  IconBell,
  IconBook,
  IconCalendar,
  IconChart,
  IconChevronDown,
  IconCompass,
  IconFilm,
  IconFlame,
  IconQuote,
  IconSettings,
  IconSparkles,
  IconUsers,
} from '../icons/icons';

const Crumb = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const CrumbSeparator = styled.span`
  color: var(--color-border-strong);
`;

const CommunitySwitcher = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const AvatarPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 4px 12px 4px 4px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const AvatarCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const AvatarName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
`;

const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-2);
`;

const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const StatTrend = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-success);
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-4);
`;

const PlanTitle = styled.h3`
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0 0 var(--space-1) 0;
  line-height: var(--line-height-snug);
`;

const PlanMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const PlanDescription = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: var(--line-height-normal);
`;

type DemoSection = 'plans' | 'customers' | 'media' | 'settings';

export const ShellDemo: React.FC = () => {
  const [active, setActive] = useState<DemoSection>('plans');

  const navGroups: ShellNavGroup[] = [
    {
      label: 'Overview',
      items: [
        {
          id: 'analytics',
          label: 'Analytics',
          icon: <IconChart />,
          onClick: () => undefined,
        },
      ],
    },
    {
      label: 'Build',
      items: [
        {
          id: 'plans',
          label: 'Plans',
          icon: <IconBook />,
          active: active === 'plans',
          onClick: () => setActive('plans'),
          badge: (
            <Tag variant="accent" size="sm">
              3
            </Tag>
          ),
        },
        { id: 'habits', label: 'Habits', icon: <IconFlame />, onClick: () => undefined },
        { id: 'quotes', label: 'Quotes', icon: <IconQuote />, onClick: () => undefined },
        { id: 'authors', label: 'Authors', icon: <IconCompass />, onClick: () => undefined },
      ],
    },
    {
      label: 'People',
      items: [
        {
          id: 'customers',
          label: 'Customers',
          icon: <IconUsers />,
          active: active === 'customers',
          onClick: () => setActive('customers'),
        },
        { id: 'events', label: 'Events', icon: <IconCalendar />, onClick: () => undefined },
        { id: 'notifications', label: 'Notifications', icon: <IconBell />, onClick: () => undefined },
      ],
    },
    {
      label: 'Studio',
      items: [
        {
          id: 'media',
          label: 'Reels & Slides',
          icon: <IconFilm />,
          active: active === 'media',
          onClick: () => setActive('media'),
          badge: (
            <Tag variant="success" size="sm" withDot>
              Live
            </Tag>
          ),
        },
      ],
    },
    {
      label: 'Workspace',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: <IconSettings />,
          active: active === 'settings',
          onClick: () => setActive('settings'),
        },
      ],
    },
  ];

  return (
    <DesignSystemSurface fillViewport>
      <AppShell
        brand={{ title: "Maria's Coaching", subtitle: 'Leadership & mindfulness' }}
        navGroups={navGroups}
        topBarCenter={
          <>
            <CommunitySwitcher>
              <span>Maria's Coaching</span>
              <IconChevronDown />
            </CommunitySwitcher>
            <Crumb>
              <CrumbSeparator>/</CrumbSeparator>
              <span style={{ textTransform: 'capitalize' }}>{active}</span>
            </Crumb>
          </>
        }
        topBarActions={
          <>
            <ThemeToggle />
            <AvatarPill>
              <AvatarCircle>M</AvatarCircle>
              <AvatarName>Maria</AvatarName>
              <IconChevronDown />
            </AvatarPill>
          </>
        }
      >
        {active === 'plans' && <PlansSection />}
        {active === 'customers' && <CustomersSection />}
        {active === 'media' && <MediaSection />}
        {active === 'settings' && <SettingsSection />}
      </AppShell>
    </DesignSystemSurface>
  );
};

const PlansSection: React.FC = () => (
  <>
    <PageHeader
      title="Plans"
      subtitle="Multi-day programs your community follows."
      actions={
        <>
          <Button variant="ghost">Templates</Button>
          <Button variant="ai" iconLeft={<IconSparkles />}>
            Generate plan
          </Button>
        </>
      }
    />

    <StatRow>
      <Card padding="md">
        <StatLabel>Active plans</StatLabel>
        <StatValue>3</StatValue>
        <StatTrend>+1 this week</StatTrend>
      </Card>
      <Card padding="md">
        <StatLabel>Days authored</StatLabel>
        <StatValue>42</StatValue>
        <StatTrend>+6 this week</StatTrend>
      </Card>
      <Card padding="md">
        <StatLabel>Avg completion</StatLabel>
        <StatValue>74%</StatValue>
      </Card>
      <Card padding="md">
        <StatLabel>Clients enrolled</StatLabel>
        <StatValue>128</StatValue>
        <StatTrend>+12 this week</StatTrend>
      </Card>
    </StatRow>

    <SectionHeader
      title="Recent plans"
      description="Most-edited programs in the last 30 days."
      actions={<Button variant="ghost">View all</Button>}
    />

    <PlanGrid>
      <Card padding="lg" interactive elevation="sm">
        <Tag variant="accent" size="sm">
          Featured
        </Tag>
        <PlanTitle>Leading through change</PlanTitle>
        <PlanDescription>
          A 21-day program for first-time managers navigating a reorg or new
          team structure.
        </PlanDescription>
        <PlanMeta>
          <span>21 days</span>
          <span>·</span>
          <span>5 habits</span>
          <span>·</span>
          <Tag variant="success" size="sm" withDot>
            Live
          </Tag>
        </PlanMeta>
      </Card>
      <Card padding="lg" interactive elevation="sm">
        <Tag variant="neutral" size="sm">
          Draft
        </Tag>
        <PlanTitle>Morning reset</PlanTitle>
        <PlanDescription>
          7 days of small actions to anchor a calmer, sharper morning rhythm.
        </PlanDescription>
        <PlanMeta>
          <span>7 days</span>
          <span>·</span>
          <span>2 habits</span>
          <span>·</span>
          <Tag variant="warning" size="sm" withDot>
            Pending review
          </Tag>
        </PlanMeta>
      </Card>
      <Card padding="lg" interactive elevation="sm">
        <Tag variant="accent" size="sm" withDot>
          AI generated
        </Tag>
        <PlanTitle>The presence practice</PlanTitle>
        <PlanDescription>
          14 days exploring attention, breath, and noticing — drafted with AI,
          edited by you.
        </PlanDescription>
        <PlanMeta>
          <span>14 days</span>
          <span>·</span>
          <span>3 habits</span>
          <span>·</span>
          <Tag variant="neutral" size="sm">
            Draft
          </Tag>
        </PlanMeta>
      </Card>
    </PlanGrid>
  </>
);

const CustomersSection: React.FC = () => (
  <>
    <PageHeader
      title="Customers"
      subtitle="People currently following one of your plans."
      actions={<Button variant="primary">Invite client</Button>}
    />
    <EmptyState
      icon={<IconUsers />}
      title="No active clients yet"
      description="Invite your first client. They'll appear here once they accept and start their plan."
      actions={
        <>
          <Button variant="primary">Invite a client</Button>
          <Button variant="ghost">Learn how invites work</Button>
        </>
      }
    />
  </>
);

const MediaSection: React.FC = () => (
  <>
    <PageHeader
      title="Reels & Slides"
      subtitle="AI-assisted media for your plan content."
      breadcrumb={<Crumb>Studio</Crumb>}
      actions={
        <>
          <Button variant="ghost">Library</Button>
          <Button variant="ai" iconLeft={<IconSparkles />}>
            New generation
          </Button>
        </>
      }
    />
    <Card padding="lg">
      <SectionHeader
        title="Currently generating"
        description="Two reels in progress for today's plan."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Skeleton variant="rect" width={96} height={64} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width="50%" />
            <Skeleton width="30%" />
          </div>
          <Tag variant="info" size="sm" withDot>
            Rendering
          </Tag>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Skeleton variant="rect" width={96} height={64} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width="60%" />
            <Skeleton width="40%" />
          </div>
          <Tag variant="info" size="sm" withDot>
            Queued
          </Tag>
        </div>
      </div>
    </Card>
  </>
);

const SettingsSection: React.FC = () => (
  <>
    <PageHeader
      title="Settings"
      subtitle="Workspace, brand, and AI guidelines."
    />
    <Card padding="lg">
      <SectionHeader
        title="Brand"
        description="How your community appears to clients."
        actions={<Button variant="secondary">Edit</Button>}
      />
      <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        This is the placeholder for the Config screen — Phase 4 migrates the
        real form here.
      </p>
    </Card>
  </>
);
